import { useEffect, useRef, useState } from "react";
import { Upload, X, FileAudio, Music, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

interface AudioUploaderProps {
  /** Public URL of an already-uploaded audio file, or empty string. */
  value: string;
  /** Optional server-side duration hint (seconds). */
  durationHintSeconds?: number | null;
  /**
   * Called with the uploaded URL and the client-measured duration on success,
   * or with ("", null) to clear.
   */
  onChange: (url: string, durationSec: number | null) => void;
  label?: string;
  maxSizeMB?: number;
  accept?: string;
  disabled?: boolean;
}

type Status = "idle" | "selected" | "uploading" | "error";

function formatErrorMessage(err: unknown, fallback = "Upload failed"): string {
  if (err && typeof err === "object") {
    const e = err as { code?: string; message?: string };
    switch (e.code) {
      case "FILE_TOO_LARGE":
        return "File is too large. Reduce size and try again.";
      case "UNSUPPORTED_MEDIA_TYPE":
        return "Audio format not recognized. Use MP3, WAV, M4A, OGG, or AAC.";
      case "R2_NOT_CONFIGURED":
        return "Storage is not configured. Contact an administrator.";
      case "ADMIN_REQUIRED":
        return "Admin session required. Please sign in again.";
      case "AUTH_REQUIRED":
        return "Your session expired. Please sign in again.";
      default:
        return e.message || fallback;
    }
  }
  return fallback;
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds)) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AudioUploader({
  value,
  durationHintSeconds = null,
  onChange,
  label = "Audio file",
  maxSizeMB = 50,
  accept = "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/ogg,audio/aac",
  disabled = false,
}: AudioUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [measuredDuration, setMeasuredDuration] = useState<number | null>(
    durationHintSeconds ?? null
  );

  // Track object URLs so we can revoke them.
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // When the parent's duration hint changes (e.g. on edit), sync it.
  useEffect(() => {
    if (durationHintSeconds != null && pickedFile == null) {
      setMeasuredDuration(durationHintSeconds);
    }
  }, [durationHintSeconds, pickedFile]);

  const measureLocalDuration = (file: File, objectUrl: string) => {
    const audio = new Audio();
    audio.preload = "metadata";
    audio.src = objectUrl;
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      setMeasuredDuration(isFinite(d) ? d : null);
    };
    audio.onerror = () => setMeasuredDuration(null);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setErrorMsg(`File exceeds ${maxSizeMB} MB limit`);
      setStatus("error");
      return;
    }
    setPickedFile(file);
    const objUrl = URL.createObjectURL(file);
    setPreviewUrl(objUrl);
    setStatus("selected");
    setErrorMsg(null);
    measureLocalDuration(file, objUrl);
  };

  const upload = async () => {
    if (!pickedFile) return;
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const res = await adminApi.uploadAudio(pickedFile);
      onChange(res.url, measuredDuration);
      setPickedFile(null);
      setPreviewUrl(null);
      setStatus("idle");
    } catch (err) {
      setErrorMsg(formatErrorMessage(err));
      setStatus("error");
    }
  };

  const remove = () => {
    onChange("", null);
    setPickedFile(null);
    setPreviewUrl(null);
    setStatus("idle");
    setErrorMsg(null);
    setMeasuredDuration(durationHintSeconds ?? null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const isUploaded = Boolean(value) && !pickedFile;
  const playbackSrc = isUploaded ? value : previewUrl;
  const filename = pickedFile?.name;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        disabled={disabled || status === "uploading"}
        className="hidden"
      />

      {playbackSrc ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
          <div className="mb-2 flex items-center gap-2">
            <FileAudio className="h-4 w-4 text-merry-pink" />
            <span className="truncate text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {filename ?? "Uploaded audio"}
            </span>
            <span className="ml-auto text-xs text-zinc-500 dark:text-zinc-400">
              {formatDuration(measuredDuration)}
            </span>
          </div>
          <audio controls src={playbackSrc} className="w-full" />
        </div>
      ) : (
        <div className="flex h-24 w-full items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
          <Music className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
        </div>
      )}

      <div className="flex items-center gap-2">
        {!value && status !== "uploading" && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            {previewUrl ? "Choose different" : `Upload ${label.toLowerCase()}`}
          </Button>
        )}
        {value && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Replace
          </Button>
        )}
        {status === "selected" && pickedFile && (
          <Button
            type="button"
            size="sm"
            onClick={upload}
            disabled={disabled}
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            Upload &amp; save
          </Button>
        )}
        {status === "uploading" && (
          <span className="inline-flex items-center text-xs text-zinc-500 dark:text-zinc-400">
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            Uploading…
          </span>
        )}
        {(value || pickedFile) && !disabled && status !== "uploading" && (
          <button
            type="button"
            onClick={remove}
            className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-red-500 dark:hover:bg-zinc-800"
            aria-label="Remove audio"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {pickedFile && status !== "uploading" && (
          <span className="truncate text-xs text-zinc-500 dark:text-zinc-400">
            {pickedFile.name} · {(pickedFile.size / 1024 / 1024).toFixed(1)} MB
          </span>
        )}
      </div>

      {errorMsg && (
        <div className="rounded-lg bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {errorMsg}
        </div>
      )}
    </div>
  );
}